import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type BankDetailsDataFragment } from '../../../api/fragments/BankDetailsData';
import {
    DeleteBankAssetDocument,
    type DeleteBankAssetMutation,
    type DeleteBankAssetMutationVariables,
} from '../../../api/mutations/deleteBankAsset';
import {
    UploadBankAssetDocument,
    type UploadBankAssetMutation,
    type UploadBankAssetMutationVariables,
} from '../../../api/mutations/uploadBankAsset';
import { BankAsset } from '../../../api/types';
import { type BankFormValues } from './BankForm';

const useHandleBankAssets = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (bankId: string, values: BankFormValues, initialValues?: BankDetailsDataFragment) => {
            const { logo, font } = values;

            const uploads = [];

            if (logo instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBankAssetMutation, UploadBankAssetMutationVariables>({
                        mutation: UploadBankAssetDocument,
                        variables: { bankId, type: BankAsset.Logo, upload: logo },
                    })
                );
            } else if (logo === null && isObject(initialValues.logo)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteBankAssetMutation, DeleteBankAssetMutationVariables>({
                        mutation: DeleteBankAssetDocument,
                        variables: { bankId, type: BankAsset.Logo },
                    })
                );
            }

            if (font instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadBankAssetMutation, UploadBankAssetMutationVariables>({
                        mutation: UploadBankAssetDocument,
                        variables: { bankId, type: BankAsset.Font, upload: font },
                    })
                );
            } else if (font === null && isObject(initialValues.font)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteBankAssetMutation, DeleteBankAssetMutationVariables>({
                        mutation: DeleteBankAssetDocument,
                        variables: { bankId, type: BankAsset.Font },
                    })
                );
            }

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useHandleBankAssets;
